import PageShell, { PageHeader } from "@/components/PageShell";
import Seo from "@/components/Seo";
import { useState, useMemo } from "react";
import { Search, Phone, Mail, HelpCircle, User, Play, Wrench } from "lucide-react";

const sections = [
  {
    key: "general",
    icon: HelpCircle,
    title: "General",
    items: [
      { q: "What is Nema TV?", a: "Nema TV is a live TV streaming service delivering channels from India, Bangladesh, Nepal and Pakistan to your favourite devices. A dedicated Arabic TV Package is coming soon." },
      { q: "Where is Nema TV available?", a: "Anywhere with a stable internet connection. Some feeds may be geo-restricted by their broadcasters." },
      { q: "What internet speed do I need?", a: "5 Mbps for SD, 10 Mbps for HD. Wired or 5 GHz Wi-Fi is recommended for large screens." },
      { q: "Can I gift a subscription?", a: "Yes — contact support@nematv.com and we'll set it up for you." },
    ],
  },
  {
    key: "account",
    icon: User,
    title: "Managing your account",
    items: [
      { q: "How do I sign up?", a: "Fill out the signup form on the homepage with your preferred duration. We'll email a secure checkout link." },
      { q: "How do I change my plan?", a: "Cancel the current one and start a new one at the end of your billing period, or write to support to switch immediately." },
      { q: "How do I update my email?", a: "Sign in, go to Account → Profile, and update the email. All future communication will use it." },
      { q: "I forgot my password.", a: "Use the 'Forgot password' link on the sign-in page. A reset link is emailed to you." },
    ],
  },
  {
    key: "watching",
    icon: Play,
    title: "Watching Nema TV",
    items: [
      { q: "Which devices are supported?", a: "Roku, Fire TV, Android TV, Android phones/tablets, iOS iPhone/iPad, and any modern web browser." },
      { q: "Can I watch on multiple devices?", a: "Yes — your subscription is for household use with concurrent stream limits per plan." },
      { q: "Is DVR available?", a: "Yes, a 3-day DVR is available on selected channels — pause, rewind and catch up." },
      { q: "Can I stream in HD?", a: "Yes, wherever the source feed supports HD. Adaptive bitrate handles slower connections." },
    ],
  },
  {
    key: "tech",
    icon: Wrench,
    title: "Technical help & troubleshooting",
    items: [
      { q: "A channel is buffering.", a: "Try switching to a wired connection, restart the app, and confirm your connection speed. If the problem persists, email support with the channel and timestamp." },
      { q: "My app won't open.", a: "Reboot the device and reinstall the app. If it still fails, note the error message and email support." },
      { q: "Playback stops after a few minutes.", a: "This is usually a network handoff issue. Toggle Wi-Fi/Ethernet and disable VPNs while streaming." },
      { q: "Audio out of sync.", a: "Restart playback and switch the audio track if available. On TVs, disable extra audio post-processing." },
      { q: "How do I contact support?", a: "Email support@nematv.com — we typically reply within a few hours during business days." },
    ],
  },
];

const HelpCenterPage = () => {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return sections;
    return sections
      .map(s => ({ ...s, items: s.items.filter(i => (i.q + i.a).toLowerCase().includes(t)) }))
      .filter(s => s.items.length > 0);
  }, [q]);

  return (
    <PageShell>
      <Seo title="Help Center — Nema TV" description="Search common questions and reach the Nema TV support team." path="/help" />
      <PageHeader eyebrow="Help center" title="How can we help?" subtitle="Search common questions or reach out — we usually reply within a few hours." />

      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="relative mb-10">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="help-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search the help center…"
            className="w-full bg-white/5 border border-white/10 rounded-full pl-11 pr-4 py-3.5 text-white placeholder:text-white/30 focus:outline-none focus:ring-2 focus:ring-[#22D3EE] focus:border-transparent"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filtered.map((s) => (
            <div key={s.key} data-testid={`help-section-${s.key}`} className="rounded-2xl border border-white/10 bg-[#0F0A19] p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-lg bg-white/5 border border-white/10 inline-flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-[#22D3EE]" />
                </div>
                <h3 className="font-display text-xl font-bold">{s.title}</h3>
              </div>
              <div className="space-y-4">
                {s.items.map((it, idx) => (
                  <div key={idx} data-testid={`help-item-${s.key}-${idx}`}>
                    <div className="text-white font-semibold text-sm">{it.q}</div>
                    <div className="text-white/60 text-sm mt-1">{it.a}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-14 rounded-3xl gradient-border p-8 md:p-10 relative">
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-white/60 mb-4">Talk to a human</div>
              <h3 className="font-display text-3xl md:text-4xl font-extrabold leading-tight">Still stuck? We&apos;re one message away.</h3>
              <p className="mt-3 text-white/60 max-w-md">Email support first — it&apos;s the fastest way to get a fix, because we can attach logs and screenshots to your ticket.</p>
            </div>
            <div className="flex flex-col gap-3">
              <a
                href="mailto:support@nematv.com"
                data-testid="help-email"
                className="inline-flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 hover:bg-white/10 transition-colors"
              >
                <Mail className="w-5 h-5 text-[#22D3EE]" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50">Email</div>
                  <div className="text-white break-all">support@nematv.com</div>
                </div>
              </a>
              <a
                href="tel:+1-000-000-0000"
                data-testid="help-phone"
                className="inline-flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-5 py-4 hover:bg-white/10 transition-colors"
              >
                <Phone className="w-5 h-5 text-[#22D3EE]" />
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-white/50">Callback request</div>
                  <div className="text-white">Email us to schedule a call</div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default HelpCenterPage;
